import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { useDisplayModeFields } from '../../hooks/useDisplayModeFields';
import FormFieldView from '../FormFieldView/FormFieldView';
import { getDocumentRequiredMessage } from '@utils/validation/document/requiredMessage';
import { UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDictFilter, IDocumentDict } from '@models/Forms/IForms';
import { getDisplayValue, parseDisplayFormat, parseMultilineDisplayFormat } from '@utils/documentUtils';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import './Dict.scss';
import { IFieldElem } from '@models/IFormData';
import { getEmptyFormValueDependencies } from '@molecules/formbuilder/utils/emptyFormValueDependencies';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormMessageReserveContext } from '../../FormMessageReserveContext';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import {
    useChangeHandlersOptional,
    useRegisterFieldChanges,
    useReportChangeError,
    useRunFieldChanges,
} from '@controls/ChangeHandlers/useChangeHandlers';
import { DictExternalDataSource } from '@utils/DictExternalDataSource';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { IPartialFormGroup } from '@srm/ui/controls/types';
import DictChipItem from '@molecules/formbuilder/controls/Dict/DictChipItem/DictChipItem';
import DictpickerControl from '@controls/DictpickerControl';
import Button from '@srm/ui/button';
import { MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import {
    useFormulaState,
    useFormulaStateWithStatus,
    useFieldSaver,
    useClearErrorOnOptional,
    useActivated,
    useFormulaDisplay,
    useLateVisibilityCascadeGate,
} from '@molecules/formbuilder/hooks';
import { processSetValues } from '@molecules/formbuilder/utils/setValuesUtils';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';

export interface IDictProps<TFieldValues extends object = object> {
    dict?: IDocumentDict;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    /**
     * Максимальное количество отображаемых чипсов
     * @default 20
     */
    maxVisibleChips?: number;
}

const Dict = <TFieldValues extends object = object>({
    dict,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    ensureExternalDataSourceLoaded,
    maxVisibleChips = 20,
}: IDictProps<TFieldValues>) => {
    const viewFields = useDisplayModeFields(isEdit || isNew ? dict?.displayMode : undefined, fields, formMethods);
    const dataField = fields[dict?.key!];
    const idField = dataField?.index;
    const { activated } = useActivated();
    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const reserveMessage = useContext(FormMessageReserveContext);
    const { value: visibilityFromFormula, settled: visibilityFromFormulaSettled } = useFormulaStateWithStatus(
        dict?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: dict
                ? {
                      targetId: dict.id || dict.key,
                      targetKind: 'field',
                      targetName: dict.name || dict.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const { value: requiredFromFormula, settled: requiredFromFormulaSettled } = useFormulaStateWithStatus(
        dict?.required,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: false,
            ensureExternalDataSourceLoaded,
            trace: dict
                ? {
                      targetId: dict.id || dict.key,
                      targetKind: 'field',
                      targetName: dict.name || dict.key,
                      ruleKind: 'required',
                  }
                : undefined,
        },
    );
    const readOnlyFromFormula = useFormulaState(dict?.readonly, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: dict
            ? {
                  targetId: dict.id || dict.key,
                  targetKind: 'field',
                  targetName: dict.name || dict.key,
                  ruleKind: 'readonly',
              }
            : undefined,
    });
    // Дебаг-оверрайды поля (жучок): forced-значения правил мимо формул.
    const fieldOverride = dict?.key ? fieldOverrides.get(dict.key) : undefined;
    const visibility = fieldOverride?.visibility ?? visibilityFromFormula;
    const visibilitySettled = fieldOverride?.visibility !== undefined || visibilityFromFormulaSettled;
    const readOnly = fieldOverride?.readonly ?? readOnlyFromFormula;
    const required = fieldOverride?.required ?? requiredFromFormula;
    const requiredSettled = fieldOverride?.required !== undefined || requiredFromFormulaSettled;
    const display = useFormulaDisplay(dict?.displayFormat, fields, formMethods, isEdit, isNew, activated, {
        fallback: dataField?.value,
        evalDisplayFormat: dict?.evalDisplayFormat,
        postProcess: (value: string) => {
            return getDisplayValue(value ?? '');
        },
        onInit: () => {
            setRawVal(dataField?.value);
        },
        ensureExternalDataSourceLoaded,
        trace: dict
            ? {
                  targetId: dict.id || dict.key,
                  targetKind: 'field',
                  targetName: dict.name || dict.key,
                  ruleKind: 'formula',
              }
            : undefined,
    });
    const onSaveField = useFieldSaver(fields, formMethods);
    const runFieldChanges = useRunFieldChanges();
    const changeHandlers = useChangeHandlersOptional();
    const reportChangeError = useReportChangeError();
    const fieldBinding = useMemo(() => ({ key: dict?.key ?? '' }), [dict?.key]);
    useRegisterFieldChanges(fieldBinding, dict?.changes!, dict?.key ?? '', fields, isEdit, isNew, onSaveField, setError, formMethods, undefined, Boolean(dict && visibility));
    const skipCentralizedCascade = useLateVisibilityCascadeGate(dict, isNew, isEdit, visibility, visibilitySettled);

    const [rawVal, setRawVal] = useState<string>('');
    const [showAllChips, setShowAllChips] = useState<boolean>(false);

    useEffect(() => {
        if (idField !== undefined && !includedFields.includes(idField)) {
            includedFields.push(idField);
        }
    }, [idField, includedFields]);

    const runNewInitCascade = useLatest(() => {
        if (skipCentralizedCascade) return;
        if (isNew && dict?.invokeChangesOnNew && visibility && !turnOffInvokeChangesOnNew) {
            const curValue = fields[dict.key];
            runFieldChanges(
                dict?.changes!,
                curValue?.value,
                dict?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        }
    });
    // Смена callbacks/карты полей не является повторной инициализацией.
    useEffect(() => runNewInitCascade.current(), [isNew, visibility, turnOffInvokeChangesOnNew, skipCentralizedCascade, runNewInitCascade]);

    const runEditInitCascade = useLatest(() => {
        if (skipCentralizedCascade) return;
        if (isEdit && dict?.invokeChangesOnEdit && visibility) {
            const curValue = fields[dict.key];
            runFieldChanges(
                dict?.changes!,
                curValue?.value,
                dict?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        }
    });
    // Смена callbacks/карты полей не является повторной инициализацией.
    useEffect(() => runEditInitCascade.current(), [isEdit, visibility, skipCentralizedCascade, runEditInitCascade]);

    const getDictAttrValue = useCallback((item: IDictionaryData, attr: string) => {
        return item.fields.find((obj) => obj.name === attr)?.value;
    }, []);

    const onSetValues = useCallback(
        async (value?: IDictionaryData[]) => {
            let displayValue: Awaited<ReturnType<typeof processSetValues>>;
            try {
                displayValue = await processSetValues({
                    setValues: dict?.setValues,
                    fields,
                    formMethods,
                    isEdit,
                    isNew,
                    value,
                    getAttrValue: getDictAttrValue,
                    displayFormat: dict?.displayFormat,
                    fieldKey: dict?.key!,
                    idField: idField!,
                    hasAddRows: true,
                    hasSourceTable: !!dict?.source?.table,
                    invokeSavedChange: async (binding, savedValue, rowData, activeTargets = new Set(), rowOldData) => {
                        return changeHandlers?.runSavedChanges(
                            binding,
                            savedValue,
                            rowData,
                            rowOldData,
                            { activeTargets },
                        ) ?? false;
                    },
                });
            } catch (error) {
                // корень каскада setValues: цели invokeChanges ошибку пробрасывают сюда
                reportChangeError(error, setError);
                return;
            }
            display.setValue(
                getDisplayValue(
                    value == null ? '' : parseDisplayFormat(displayValue, fields, ',', dict?.evalDisplayFormat).text,
                ),
            );
        },
        [
            dict?.setValues,
            dict?.displayFormat,
            dict?.key,
            dict?.source?.table,
            fields,
            formMethods,
            isEdit,
            isNew,
            idField,
            getDictAttrValue,
            changeHandlers,
            reportChangeError,
            setError,
        ],
    );

    const getFormValuesAsync = async () => {
        let formDataParams = dict?.formValues;
        const map = {} as Record<string, any>;
        if (formDataParams) {
            for (const ele of formDataParams.formValue) {
                if (ele.formula) {
                    let manager = new FormulaManager(ele.formula);
                    manager.Init(fields, formMethods);
                    map[ele.key] = await manager.ReplaceFormulaValues(isEdit, isNew);
                } else {
                    let field = fields[ele.attr];
                    if (!field) {
                        map[ele.key] = undefined;
                        continue;
                    }
                    let fname = ('fields.[' + field.index + '].value') as any;
                    let val = formMethods.getValues(fname);
                    if (ele.column) {
                        if (ele.function === '{join}') {
                            map[ele.key] = (val as [])?.map((item: any) => `'${item[ele.column]}'`).join(',') ?? val;
                        } else {
                            map[ele.key] = (val as [])?.map((item: any) => item[ele.column]) ?? val;
                        }
                    } else {
                        map[ele.key] = val;
                    }
                }
            }
        }
        return JSON.stringify(map);
    };

    const getEmptyDependencies = () => getEmptyFormValueDependencies(dict?.formValues?.formValue, fields, formMethods);

    const getFiltersAsync = async () => {
        let filters = dict?.filters;
        let result = {} as IDictFilter;
        if (filters) {
            for (const ele of filters.filter) {
                let conditionMng = new FormulaManager(ele.condition);
                conditionMng.Init(fields, formMethods);
                if (await conditionMng.EvalFormulaValues(isEdit, isNew)) {
                    result.condition = ele.condition;
                    if (ele.filter) {
                        let filterMng = new FormulaManager(ele.filter);
                        filterMng.Init(fields, formMethods);
                        result.filter = (await filterMng.ReplaceFormulaValues(isEdit, isNew))!;
                    }
                    if (ele.script) {
                        let scriptMng = new FormulaManager(ele.script);
                        scriptMng.Init(fields, formMethods);
                        result.script = (await scriptMng.ReplaceFormulaValues(isEdit, isNew))!;
                    }
                }
            }
        }
        return result;
    };

    const getExternalDataSource = (dict: IDocumentDict) => {
        const dictExternalDataSource = new DictExternalDataSource(dict.externalDataSource);
        return dictExternalDataSource.GetData(formMethods, fields);
    };

    const onChangeDictValue = async (value?: any) => {
        setRawVal(value);
        await runFieldChanges(
            dict?.changes!,
            value,
            dict?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
            fieldBinding,
        );
    };
    useClearErrorOnOptional(required, requiredSettled, formMethods, dataField);

    const toggleShowAllChips = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowAllChips((prev) => !prev);
    }, []);

    const parsedViewDisplay = useMemo(
        () =>
            dict?.isMultiple && dict?.multilineDisplayOnView
                ? parseMultilineDisplayFormat(dict?.displayFormat, viewFields, dict?.evalDisplayFormat)
                : parseDisplayFormat(dict?.displayFormat, viewFields, ',', dict?.evalDisplayFormat),
        [dict?.displayFormat, dict?.evalDisplayFormat, dict?.isMultiple, dict?.multilineDisplayOnView, viewFields],
    );

    // View mode chips
    const viewModeChips = useMemo(() => {
        const allChips = parsedViewDisplay.values;

        // If showAllChips is true or there are fewer chips than maxVisibleChips, show all
        if (showAllChips || allChips.length <= maxVisibleChips) {
            return allChips;
        }

        // Otherwise, show only the first maxVisibleChips
        return allChips.slice(0, maxVisibleChips);
    }, [maxVisibleChips, parsedViewDisplay.values, showAllChips]);

    // Determine if we need to show the "Show More" button
    const hasMoreChips = useMemo(() => {
        const allChips = parsedViewDisplay.values;
        return allChips.length > maxVisibleChips;
    }, [maxVisibleChips, parsedViewDisplay.values]);

    const effectiveRequired =
        debugSettings.skipAllValidation || debugSettings.skipRequiredValidation ? false : required;

    const renderView = () =>
        !dict ? null : (
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={isEmptyDisplayValue(parsedViewDisplay.text)}
                inLineFormat={dict.isValue}
                label={dict.name}
                wrapNewLines={dict?.multilineDisplayOnView}
                title={dict.viewTitle}
                testid={dict.key}
                verticalBottomAlign={dict?.verticalBottomAlign}
            >
                {dict.chipsView ? (
                    <div className="dict-chips-container">
                        <div className="dict-chips-items">
                            {viewModeChips.map((item: string, index: number) => (
                                <DictChipItem
                                    key={index}
                                    item={item}
                                    showRemoveIcon={false}
                                    /*onRemove={() => {}}*/
                                />
                            ))}
                            {hasMoreChips && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={toggleShowAllChips}
                                    className="dict-chips-show-more"
                                    startAdornment={
                                        showAllChips ? (
                                            <MdOutlineExpandLess size="20" />
                                        ) : (
                                            <MdOutlineExpandMore size="20" />
                                        )
                                    }
                                >
                                    {showAllChips
                                        ? 'Скрыть'
                                        : `Ещё ${parsedViewDisplay.values.length - maxVisibleChips}`}
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    parsedViewDisplay.text
                )}
            </FieldWrapper>
        );

    return dict && dict.key && visibility && idField != undefined ? (
        <div
            className={clsx(
                'form-field',
                (isEdit || isNew) && dict.displayMode !== 'view' && readOnly && 'form-field--readonly',
            )}
            data-testid={dict.id ? `dict-${dict.id}` : undefined}
            data-document-field-index={dataField.index}
            {...formulaTraceAttrs(dict.id || dict.key, 'field')}
        >
            {debugSettings.showFieldFormulas && (
                <FieldFormulaTooltip
                    required={dict.required}
                    visibilityRules={dict.visibilityRules}
                    readonly={dict.readonly}
                    fieldKey={dict.key}
                    isViewMode={!isEdit && !isNew}
                    rhfPath={dataField?.name}
                    value={dataField?.value}
                    requiredResult={requiredFromFormula}
                    readonlyResult={readOnlyFromFormula}
                    visibilityResult={visibilityFromFormula}
                />
            )}
            {(isEdit || isNew) && dict.displayMode === 'view' ? (
                <FormFieldView
                    name={`fields.[${idField}].value`}
                    control={formMethods.control}
                    rules={{
                        required: { value: effectiveRequired, message: getDocumentRequiredMessage(dict, 'dict') },
                    }}
                >
                    {renderView}
                </FormFieldView>
            ) : isEdit || isNew ? (
                <DictpickerControl
                    label={dict?.name}
                    tooltip={<ToolTipTitle title={dict?.title} />}
                    modalTitle={dict?.modalTitle}
                    formGroupProps={
                        { verticalBottomAlign: dict?.verticalBottomAlign, reserveMessage } as IPartialFormGroup
                    }
                    docId={docId}
                    externalSearch={dict?.externalSearch}
                    addForm={dict?.addForm}
                    dictionaryName={dict?.dictName!}
                    setValues={dict?.setValues}
                    isFormData={dict?.isFormData}
                    isMultiple={dict?.isMultiple}
                    recursive={dict?.recursive}
                    isSelectMode={dict?.isSelectMode}
                    getExternalDataSource={dict?.externalDataSource ? () => getExternalDataSource(dict) : undefined}
                    getFormValuesAsync={getFormValuesAsync}
                    getFiltersAsync={getFiltersAsync}
                    getEmptyDependencies={getEmptyDependencies}
                    required={effectiveRequired}
                    rules={{
                        required: {
                            value: effectiveRequired ? effectiveRequired : false,
                            message: getDocumentRequiredMessage(dict ?? {}, 'dict'),
                        },
                    }}
                    readOnly={readOnly}
                    displayValue={display.value}
                    rawVal={rawVal}
                    gridAttribute={dict?.gridAttribute}
                    startLevel={dict?.startLevel}
                    name={`fields.[${idField}].value`}
                    onSetValues={onSetValues}
                    control={formMethods.control}
                    insideGrid={false}
                    onDictValueChanged={onChangeDictValue}
                    selectableLevels={dict?.selectableLevels}
                    visibleLevels={dict?.visibleLevels}
                    predicatesCache={dict?.predicatesCache}
                    loadMode={dict?.loadMode}
                    joinedDictionaries={dict?.joinedDictionaries}
                    source={dict?.source}
                    useClientSideDataProcessing={dict?.useClientSideDataProcessing}
                    chipsView={dict.chipsView}
                    placeholder={dict?.placeholder ? dict.placeholder : undefined}
                    userScripts={dict?.userScripts}
                    maxVisibleChips={maxVisibleChips}
                    testid={dict.key}
                    wordWrapEnabled={dict?.wordWrapEnabled}
                    showActionsBar={dict?.showActionsBar}
                />
            ) : (
                renderView()
            )}
        </div>
    ) : (
        <></>
    );
};

export default Dict;
